/**
 * Text Constants for Get My News
 * All visible text content centralized for easy management
 */

export const TEXTS = {
    // ========================================
    // BRAND & GENERAL
    // ========================================
    app: {
        name: 'Get My News',
        tagline: 'Daily curated news for the modern world',
        description: 'A subscription-based news aggregation platform delivering curated daily content across multiple topics with advanced anti-scraping protection.',
        copyright: '© 2024 PIXMELT. All rights reserved.'
    },

    // ========================================
    // LANDING PAGE (DOMAIN FOR SALE)
    // ========================================
    landing: {
        title: 'get-my-news.com',
        badge: 'PREMIUM DOMAIN FOR SALE',
        headline: 'Your Next News Empire Starts Here',
        price: '300',
        currency: 'EUR',
        subheadline: 'A memorable, brandable domain perfect for news aggregation platforms, media startups, newsletter businesses, or AI-powered news services.',
        features: [
            'Instantly recognizable and easy to remember',
            'Perfect for news, media, or content platforms',
            'Great for newsletters, RSS aggregators, or AI news tools',
            'SEO-friendly with strong keyword relevance',
            'Ready for immediate use or development'
        ],
        useCases: {
            title: 'Ideal Use Cases',
            items: [
                { icon: 'newspaper', label: 'News Aggregation Platform' },
                { icon: 'envelope', label: 'Newsletter Service' },
                { icon: 'robot', label: 'AI-Powered News Curation' },
                { icon: 'rss', label: 'RSS Feed Reader' },
                { icon: 'chart-line', label: 'Media Analytics Dashboard' },
                { icon: 'globe', label: 'International News Portal' }
            ]
        },
        cta: {
            primary: 'Buy Now on Sedo',
            secondary: 'Make an Offer',
            url: 'https://sedo.com/search/details/?domain=get-my-news.com'
        },
        secureTransaction: 'Secure transaction via Sedo.com - the world\'s largest domain marketplace',
        footer: 'Don\'t miss this opportunity to own a premium domain in the news and media space.',
        // Legacy fields for compatibility
        emailPlaceholder: 'your@email.com',
        submitButton: 'Get notified',
        privacyNote: "We don't spam. One email at launch.",
        successMessage: "Thank you! You'll be notified at launch.",
        errorMessage: 'An error occurred. Please try again.',
        invalidEmail: 'Please enter a valid email address.',
        legalLinks: {
            privacy: 'Privacy Policy',
            terms: 'Legal Notice'
        }
    },

    // ========================================
    // NAVIGATION
    // ========================================
    navigation: {
        home: 'Home',
        news: 'News',
        topics: 'Topics',
        archive: 'Archive',
        pricing: 'Pricing',
        dashboard: 'Dashboard',
        profile: 'Profile',
        settings: 'Settings',
        logout: 'Logout',
        login: 'Login',
        signup: 'Sign Up'
    },

    // ========================================
    // NEWS TOPICS
    // ========================================
    topics: {
        politics: 'Politics',
        economy: 'Economy',
        finance: 'Finance',
        ai: 'Artificial Intelligence',
        crypto: 'Crypto & Blockchain',
        ecology: 'Ecology',
        itDevelopment: 'IT Development',
        science: 'Science',
        // Premium topics
        travel: 'Travel',
        music: 'Music',
        humanityCrisis: 'Humanity Crisis',
        people: 'People',
        sport: 'Sport',
        immigration: 'Immigration',
        // Prospective topics
        cyber: 'Cyber',
        wars: 'Wars'
    },

    // ========================================
    // SUBSCRIPTION SYSTEM
    // ========================================
    subscription: {
        tiers: {
            free: 'Free',
            starter: 'Starter Pack',
            premium: 'Premium'
        },
        prices: {
            starter: '$3/month',
            humanityCrisis: '+$1/month',
            people: '+$1/month',
            sport: '+$1/month',
            immigration: '+$3/month',
            extendedSharing: '+$4/month',
            customSharePage: '+$12/month',
            mcpConnection: '+$14/month'
        },
        features: {
            dailyAccess: 'Daily news access',
            previousDay: 'Previous day access (D-1)',
            archiveAccess: 'Last 10 days archive',
            articleSaving: 'Save articles (12 months)',
            basicSharing: 'Share links (5 days)',
            extendedSharing: 'Share links (1 month)',
            customPage: 'Custom sharing page',
            cashback: 'Cashback $1/month per 1,000 visits',
            mcpIntegration: 'MCP connection',
            askTopics: 'Ask new topics',
            askQuestions: 'Submit questions'
        },
        actions: {
            subscribe: 'Subscribe Now',
            upgrade: 'Upgrade',
            manage: 'Manage Subscription',
            cancel: 'Cancel Subscription',
            resume: 'Resume Subscription'
        }
    },

    // ========================================
    // AUTHENTICATION
    // ========================================
    auth: {
        login: {
            title: 'Login to Get My News',
            email: 'Email address',
            password: 'Password',
            submit: 'Sign In',
            forgotPassword: 'Forgot your password?',
            noAccount: "Don't have an account?",
            signupLink: 'Sign up here'
        },
        signup: {
            title: 'Create Your Account',
            firstName: 'First name',
            lastName: 'Last name',
            email: 'Email address',
            password: 'Password',
            confirmPassword: 'Confirm password',
            submit: 'Create Account',
            hasAccount: 'Already have an account?',
            loginLink: 'Sign in here',
            terms: 'By creating an account, you agree to our Terms of Service and Privacy Policy.'
        },
        forgotPassword: {
            title: 'Reset Your Password',
            email: 'Email address',
            submit: 'Send Reset Link',
            backToLogin: 'Back to login'
        }
    },

    // ========================================
    // NEWS CONTENT
    // ========================================
    news: {
        today: 'Today\'s News',
        yesterday: 'Yesterday\'s News',
        archive: 'News Archive',
        readMore: 'Read More',
        readFull: 'Read Full Article',
        share: 'Share',
        save: 'Save Article',
        saved: 'Saved',
        remove: 'Remove',
        source: 'Source',
        publishedAt: 'Published',
        timeAgo: {
            justNow: 'Just now',
            minutesAgo: '{{count}} minutes ago',
            hoursAgo: '{{count}} hours ago',
            daysAgo: '{{count}} days ago'
        }
    },

    // ========================================
    // USER INTERFACE
    // ========================================
    ui: {
        loading: 'Loading...',
        search: 'Search',
        filter: 'Filter',
        sort: 'Sort',
        close: 'Close',
        open: 'Open',
        back: 'Back',
        next: 'Next',
        previous: 'Previous',
        submit: 'Submit',
        cancel: 'Cancel',
        save: 'Save',
        edit: 'Edit',
        delete: 'Delete',
        confirm: 'Confirm',
        retry: 'Retry',
        refresh: 'Refresh'
    },

    // ========================================
    // ERROR MESSAGES
    // ========================================
    errors: {
        general: 'Something went wrong. Please try again.',
        network: 'Network error. Please check your connection.',
        unauthorized: 'You need to be logged in to access this content.',
        forbidden: 'You don\'t have permission to access this content.',
        notFound: 'The content you\'re looking for was not found.',
        subscriptionRequired: 'This feature requires a subscription.',
        rateLimited: 'Too many requests. Please try again later.',
        invalidCredentials: 'Invalid email or password.',
        emailExists: 'An account with this email already exists.',
        passwordMismatch: 'Passwords do not match.',
        invalidEmail: 'Please enter a valid email address.',
        passwordTooShort: 'Password must be at least 8 characters long.',
        paymentFailed: 'Payment failed. Please try again or use a different payment method.'
    },

    // ========================================
    // SUCCESS MESSAGES
    // ========================================
    success: {
        accountCreated: 'Account created successfully! Welcome to Get My News.',
        loginSuccess: 'Welcome back to Get My News!',
        logoutSuccess: 'You have been logged out successfully.',
        passwordReset: 'Password reset email sent. Check your inbox.',
        subscriptionUpdated: 'Subscription updated successfully.',
        articleSaved: 'Article saved to your reading list.',
        articleRemoved: 'Article removed from your reading list.',
        profileUpdated: 'Profile updated successfully.'
    },

    // ========================================
    // SHARING
    // ========================================
    sharing: {
        title: 'Share this article',
        copyLink: 'Copy Link',
        linkCopied: 'Link copied to clipboard!',
        socialNetworks: {
            facebook: 'Share on Facebook',
            twitter: 'Share on X (Twitter)',
            instagram: 'Share on Instagram',
            tiktok: 'Share on TikTok',
            discord: 'Share on Discord',
            reddit: 'Share on Reddit',
            linkedin: 'Share on LinkedIn',
            email: 'Share via Email'
        },
        customPage: {
            title: 'Custom Sharing Page',
            description: 'Create your own branded sharing page',
            visits: 'Visits',
            earnings: 'Earnings'
        }
    },

    // ========================================
    // DASHBOARD
    // ========================================
    dashboard: {
        welcome: 'Welcome back, {{name}}!',
        todaysNews: 'Today\'s Headlines',
        savedArticles: 'Saved Articles',
        readingHistory: 'Reading History',
        subscription: 'Your Subscription',
        analytics: 'Analytics',
        quickActions: 'Quick Actions'
    },

    // ========================================
    // SETTINGS
    // ========================================
    settings: {
        account: 'Account Settings',
        profile: 'Profile',
        preferences: 'Preferences',
        privacy: 'Privacy',
        billing: 'Billing',
        topics: {
            title: 'Topic Preferences',
            description: 'Choose which topics you want to see in your daily news feed'
        },
        notifications: {
            email: 'Email Notifications',
            push: 'Push Notifications',
            daily: 'Daily News Summary',
            breaking: 'Breaking News'
        }
    }
} as const;